import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    MultiSelect,
    MultiSelectOption,
    Button,
} from "@utahdts/utah-design-system";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@apollo/client";
import { GET_WATER_USE_SYSTEM_USAGE_YEARS_SOURCES } from "../../../queries/engineeringQrys";
import SkeletonLoader from "../../reuseableComponents/loaders/SkeletonLoader";

const DwriDataSearch = () => {
    // Remove invalid aria-selected from Utah Design System multi-select tags for accessibility compliance
    useEffect(() => {
        const removeInvalidAriaSelected = () => {
            const tags = document.querySelectorAll('.multi-select__tag-wrapper[aria-selected]');
            tags.forEach(tag => tag.removeAttribute('aria-selected'));
        };
        removeInvalidAriaSelected();
        const observer = new MutationObserver(removeInvalidAriaSelected);
        observer.observe(document.body, { subtree: true, childList: true });
        return () => observer.disconnect();
    }, []);
    const [selectedYears, setSelectedYears] = useState([]);
    const [selectedSources, setSelectedSources] = useState([]);
    const selectedWaterSystem = JSON.parse(sessionStorage.getItem("selectedWaterSystem") || "{}");
    const [yearOptions, setYearOptions] = useState([]);
    const [sourceOptions, setSourceOptions] = useState([]);
    const history = useHistory();
    const { data, loading } = useQuery(GET_WATER_USE_SYSTEM_USAGE_YEARS_SOURCES, {
        variables: { pwsId: selectedWaterSystem.pwsId },
    });

    const handleView = () => {
        const hasData = yearOptions && yearOptions.length > 0;
        let path = "/engineering/dwri/dwri-data-result";

        if (hasData) {
            path += "/dwri-data";
        } else {
            path += "/calculations";
        }

        history.push(path, {
            selectedWaterSystem,
            selectedYears,
            selectedSources,
            yearOptions
        });
    };

    // set yearOptions and sourceOptions when data loads
    useEffect(() => {
        if (data) {
            const yearsSourcesData = data.getWaterUseSystemUsageYearsSources;
            if (!yearsSourcesData) return;
            const { years, sources } = yearsSourcesData;
            setYearOptions(years);
            const sourcesArray = sources.map(source => ({ value: String(source.id), label: source.name }));
            setSourceOptions(sourcesArray);
            // Set all sources as selected by default
            setSelectedSources(sourcesArray.map(source => source.value));
        }
    }, [data]);

    if (loading) {
        return (
            <div className="utah-design-system page-container">
                <SkeletonLoader width="100%" height="200px" />
            </div>
        );
     }
    return (
        <div className="utah-design-system page-container">
            <div style={{ maxWidth: "600px" }}>
                <MultiSelect
                    id="dwri-years"
                    isClearable
                    label="Select Years"
                    labelClassName="visually-hidden"
                    placeholder={selectedYears.length === 0 ? "Select Years" : ""}
                    values={selectedYears}
                    onChange={setSelectedYears}
                >
                    {yearOptions.map((year) => (
                        <MultiSelectOption key={year} label={String(year)} value={String(year)} />
                    ))}
                </MultiSelect>
            </div>
            <p className="text-muted fst-italic mb-3">
                <small>The last five years of data will be selected if no years are selected.</small>
            </p>

            <div style={{ maxWidth: "600px" }}>
                <MultiSelect
                    id="dwri-sources"
                    isClearable
                    label="Select System Sources"
                    labelClassName="visually-hidden"
                    placeholder={selectedSources.length === 0 ? "Select System Sources" : ""}
                    values={selectedSources}
                    onChange={setSelectedSources}
                >
                    {sourceOptions.map((source) => (
                        <MultiSelectOption key={source.value} label={source.label} value={source.value} />
                    ))}
                </MultiSelect>
            </div>
            <p className="text-muted fst-italic mb-3">
                <small>All sources will be included if no sources are removed.</small>
            </p>

            <Button variant="primary" onClick={handleView}>
                View <FontAwesomeIcon className="font-icon" icon={faSearch} />
            </Button>
            {yearOptions.length === 0 && (
                <div>
                    <br />
                    <p className="text-danger">
                        <strong>A system with the ID of {selectedWaterSystem.alternateNum} returned no DWRi data.</strong>
                    </p>
                </div>
            )}
        </div>
    );
};

export default DwriDataSearch;
